/* eslint-disable class-methods-use-this */
import { StoredImage } from 'app/interfaces/StudyStore';
import {
  FindQueryModel,
  GetQueryModel,
  PACSCommunication,
  PACSError,
} from 'app/interfaces/PACSCommunication';
import { Rect, Size, StoredImageInfo } from 'app/interfaces/Image';
import { DicomBlob, ImageLoader, LoadedImage } from 'app/interfaces/ImageLoader';
import picoxiaDicom from 'app/native/node-addons/picoxia-dicom';
import { DicomData, DicomTransferSyntax } from 'app/interfaces/Dicom';
import logger from 'app/utils/debug/logger';
import { format } from 'date-fns';
import _ from 'lodash';
import { getDicomValue } from 'app/utils/dicom/DicomData';
import convertCropAnnotationToCropRectWithScale from 'app/utils/image/convertCropAnnotationToCropRectWithScale';
import {
  TransformableImage,
  convertDisplayableImageToTransformableImage,
  transformImage,
} from 'app/utils/imageManipulation/ImageTransformation';
import Image, { InterpolationAlgorithm } from 'image-js';
// @ts-ignore
import copyImage from 'image-js/src/image/internal/copy';
import { Transformation } from 'app/interfaces/Image/Transformation';
import { viewport } from '@popperjs/core';

export class NotPACSImageError extends Error {
  constructor() {
    super('Image is not from PACS');
  }
}
export class NoImageError extends Error {
  constructor() {
    super('No image found');
  }
}

const DETECTOR_IMAGE_DIMENSIONS: Record<string, Size> = {
  // Thales
  '3543EZhd': { height: 4302, width: 3534 },
  // IRay
  Luna1012X: { height: 2512, width: 3162 },
  Luna1013XE: { height: 2560, width: 3328 },
  Luna1417X: { height: 4352, width: 3584 },
  Luna1717X: { height: 4267, width: 4267 },
  Mars1012V: { height: 2048, width: 2448 },
  Mars1012X: { height: 2512, width: 3162 },
  Mars1417V: { height: 2800, width: 2304 },
  Mars1417V2: { height: 2800, width: 2304 },
  Mars1417V3: { height: 2844, width: 2304 },
  Mars1417X: { height: 4352, width: 3584 },
  Mars1417XF: { height: 2840, width: 2342 },
  'Mars1417XF_192.168.100.8': { height: 2840, width: 2342 },
  'Mars1417XF_192.168.8.8': { height: 2840, width: 2342 },
  Mars1417XM: { height: 4352, width: 3584 },
  Mars1717V: { height: 3072, width: 3072 },
  Mars1717V2: { height: 3072, width: 3072 },
  DemoMars1717V3: { height: 3072, width: 3072 },
  Mars1717V3: { height: 3072, width: 3072 },
  Mars1717VN: { height: 3072, width: 3072 },
  Mars1717X: { height: 4267, width: 4267 },
  Mars1717X2: { height: 4267, width: 4267 },
  Mars1717XF: { height: 2840, width: 2848 },
  'Mars1717XF_192.168.100.8': { height: 2840, width: 2848 },
  'Mars1717XF_192.168.8.8': { height: 2840, width: 2848 },
  Mars1724V: { height: 3072, width: 4352 },
  Venu1012V: { height: 2048, width: 2448 },
  Venu1717X: { height: 3072, width: 3072 },
  Venu1717XV: { height: 3072, width: 3072 },
  Venu1748V: { height: 8704, width: 3072 },
};

function transformProcessingRect(
  rect: Rect,
  viewport: StoredImageInfo['viewport'],
  imageDimensions: Size
): Rect {
  // Transform the processing rect to the final image coordinate system
  // based on the viewport properties (flip, rotation, etc.)
  const { width, height } = imageDimensions;
  const finalRect = { ...rect };
  if (viewport.hflip) {
    finalRect.x = width - rect.x - rect.width;
  }
  if (viewport.vflip) {
    finalRect.y = height - rect.y - rect.height;
  }
  if (viewport.rotation) {
    // For rotation, we need to adjust the coordinates based on the rotation angle
    switch (viewport.rotation % 360) {
      case 90:
        finalRect.x = rect.y;
        finalRect.y = width - rect.x - rect.width;
        finalRect.width = rect.height;
        finalRect.height = rect.width;
        break;
      case 180:
        finalRect.x = width - rect.x - rect.width;
        finalRect.y = height - rect.y - rect.height;
        break;
      case 270:
        finalRect.x = height - rect.y - rect.height;
        finalRect.y = rect.x;
        finalRect.width = rect.height;
        finalRect.height = rect.width;
        break;
      default:
      // No rotation or unsupported rotation
    }
  }
  return finalRect;
}

function unTransformImage(img: TransformableImage, transformations: Transformation) {
  const { rotation, hflip, vflip } = transformations;

  if (rotation)
    img = img.rotate(-rotation, {
      interpolation: 'bilinear' as InterpolationAlgorithm.BILINEAR,
    });
  if (vflip) img = img.flipY();
  if (hflip) img = img.flipX();

  return img;
}

function adaptPicoxiaProcessedImageToImageSize(
  loadedImage: LoadedImage,
  imageInfo: StoredImageInfo
): LoadedImage {
  if (imageInfo.origin !== 'acquisition') return loadedImage;
  // full image size must be retrieved from imageInfo using viewport
  // Crop area must be retrieved from processingRect or annotations
  // Then image must be extended to full image size present in viewport

  let originalImageDimensions = imageInfo.detectorInfo?.dimensions;
  if (!originalImageDimensions?.width || !originalImageDimensions?.height) {
    // If dimensions are not provided, we try to find them from the detector model
    originalImageDimensions = DETECTOR_IMAGE_DIMENSIONS[imageInfo.detectorInfo?.productModel];
  }
  if (!originalImageDimensions?.width || !originalImageDimensions?.height) {
    throw new Error(
      `No original image dimensions found for detector ${imageInfo.detectorInfo?.productModel}`
    );
  }
  const { width, height } = originalImageDimensions;

  let processingRect = imageInfo.image_metadata?.processingRect;
  if (!processingRect) {
    const cropAnnotations = imageInfo.annotations?.Crop;
    if (!cropAnnotations) {
      // Early exit since no need to crop so no problems with image size
      return loadedImage;
    }
    const cropRect = convertCropAnnotationToCropRectWithScale(
      cropAnnotations,
      imageInfo.viewport,
      originalImageDimensions
    );
    if (!cropRect) {
      // Case that should not happen since empty crop annotations should not exist.
      return loadedImage;
    }
    const [x, y, width, height] = cropRect;
    processingRect = { x, y, width, height };
  }
  processingRect = {
    x: Math.round(processingRect.x),
    y: Math.round(processingRect.y),
    width: Math.round(processingRect.width),
    height: Math.round(processingRect.height),
  };

  // Crop annotation or processing rect coords are in the original image coordinate system.
  // loadedImage dimensions are in the final image coordinate system (after flip and rotation).
  // We need to adapt the processing rect to the loaded image dimensions.
  const transformedProcessingRect = transformProcessingRect(
    processingRect,
    imageInfo.viewport,
    originalImageDimensions
  );

  // Due to possible jpeg width could have a small difference due to rounding errors.
  if (
    Math.abs(transformedProcessingRect.width - loadedImage.imageData.width) > 1 ||
    Math.abs(transformedProcessingRect.height - loadedImage.imageData.height) > 1
  ) {
    // We fail since the cropped area and the saved area does not match so we cannot adapt the image.
    const loadedDimensionsRepr = JSON.stringify(_.pick(loadedImage.imageData, ['width', 'height']));
    const processingrectRepr = JSON.stringify(_.pick(processingRect, ['width', 'height']));
    throw new Error(
      `Stored Dicom image size does not match the processedArea size : ${loadedDimensionsRepr} != ${processingrectRepr}`
    );
  }
  let croppedImage = convertDisplayableImageToTransformableImage(loadedImage.imageData);
  // Here we must untransform image to the original image coordinate system
  croppedImage = unTransformImage(
    croppedImage,
    _.pick(imageInfo.viewport, ['rotation', 'hflip', 'vflip'])
  );
  const fullImage = Image.createFrom(croppedImage, {
    width: originalImageDimensions.width,
    height: originalImageDimensions.height,
  });
  copyImage(croppedImage, fullImage, processingRect.x, processingRect.y);
  const minPixelValue = croppedImage.getMin()[0];
  const maxPixelValue = croppedImage.getMax()[0];
  return {
    isNotProcessable: true,
    dicomData: {
      ..._.omit(loadedImage.dicomData, ['pixelData', 'Rows', 'Columns']),
      Rows: height,
      Columns: height,
    },
    imageData: {
      ...loadedImage.imageData,
      imageId: `rawUInt16:${imageInfo._id}`,
      getPixelData: () => fullImage.data as Uint16Array,
      minPixelValue,
      maxPixelValue,
      rows: height,
      columns: width,
      height,
      width,
      color: false,
      sizeInBytes: width * height * 2,
      getImageData: undefined,
      getCanvas: undefined,
      getImage: undefined,
      rgba: false,
      // Below value used value from imageData
      // slope: 1,
      // intercept: 0,
      // columnPixelSpacing,
      // rowPixelSpacing,
      // invert: false,
      // lut: undefined,
      // cachedLut: undefined,}
    },
  };
}

export default class PACSStudyStoreAdataper {
  constructor(public pacsCommunication: PACSCommunication, public imageLoader: ImageLoader) {}

  loadImage = async (
    studyId: string,
    imageId: string,
    imageInfo: StoredImageInfo
  ): Promise<StoredImage | NotPACSImageError | NoImageError> => {
    if (!this.pacsCommunication) {
      throw new Error('PACS communication is not initialized');
    }
    let sopInstanceUID = imageInfo.sop_instance_uid;
    let seriesInstanceUID = undefined;
    let studyInstanceUID = undefined;
    if (!sopInstanceUID || !seriesInstanceUID || !studyInstanceUID) {
      const { acquisitionTime } = imageInfo;
      const dicomAcquisitionDate = format(acquisitionTime as Date, 'yyyyMMdd');
      const dicomAcquisitionTime = format(acquisitionTime as Date, 'kkmmss');

      const possibleStudies = (await this.pacsCommunication.find(
        {
          StudyID: studyId,
          StudyInstanceUID: undefined,
          QueryRetrieveLevel: 'STUDY',
        },
        FindQueryModel.Study
      )) as DicomData[];

      const possibleStudyInstanceUIDs = possibleStudies.map((study) =>
        getDicomValue(study.StudyInstanceUID)
      );
      if (!possibleStudyInstanceUIDs.length) {
        logger.info('No PACS study found', imageInfo);
        throw new NoImageError();
      }
      console.debug('Found PACS studies', { possibleStudies, imageInfo });

      const studiesImages = _.flatten(
        await Promise.all(
          possibleStudyInstanceUIDs.map((studyInstanceUID) =>
            (
              this.pacsCommunication.find(
                {
                  StudyInstanceUID: studyInstanceUID,
                  SeriesInstanceUID: undefined,
                  StudyID: undefined,
                  SOPInstanceUID: undefined,
                  AcquisitionDate: undefined,
                  AcquisitionTime: undefined,
                  ContentDate: undefined,
                  ContentTime: undefined,
                  QueryRetrieveLevel: 'IMAGE',
                },
                FindQueryModel.Study
              ) as Promise<DicomData[]>
            ).catch((e) => {
              logger.error('Failed to find images for study', {
                studyInstanceUID,
                error: e,
              });
              return [];
            })
          )
        )
      );

      const possibleMatchingImages = _.filter(
        studiesImages,
        ({ AcquisitionDate, AcquisitionTime }) => {
          return (
            getDicomValue(AcquisitionDate) === dicomAcquisitionDate &&
            getDicomValue(AcquisitionTime)?.split('.')[0] === dicomAcquisitionTime
          );
        }
      );
      console.info('Found PACS images', { possibleMatchingImages, imageInfo });
      const matchingImage = _.last(
        _.sortBy(
          possibleMatchingImages,
          ({ ContentDate, ContentTime }) =>
            `${getDicomValue(ContentDate)}-${getDicomValue(ContentTime)}`
        )
      );
      sopInstanceUID = getDicomValue(matchingImage?.SOPInstanceUID);
      seriesInstanceUID = getDicomValue(matchingImage?.SeriesInstanceUID);
      studyInstanceUID = getDicomValue(matchingImage?.StudyInstanceUID);
    }
    if (!sopInstanceUID) {
      logger.info('Image is not from PACS', imageInfo);
      throw new NotPACSImageError();
    }
    try {
      const result = await this.pacsCommunication.get(
        {
          SOPInstanceUID: sopInstanceUID,
          SeriesInstanceUID: seriesInstanceUID,
          StudyInstanceUID: studyInstanceUID,
          QueryRetrieveLevel: 'IMAGE',
        },
        GetQueryModel.Study
      );
      if (!result) {
        logger.info('No PACS image found', imageInfo);
        throw new NoImageError();
      } else if ((result as PACSError).status) {
        throw new Error((result as PACSError).status);
      }

      const pacsImage = (result as DicomData[])[0];

      const dicomBuffer = await picoxiaDicom()?.writeDicomAsync(
        pacsImage,
        undefined,
        DicomTransferSyntax.ImplicitVRLittleEndian
      );
      const imageBlob = new DicomBlob([dicomBuffer]);
      const loadedImage = await this.imageLoader.load(imageBlob);
      if (!loadedImage) {
        logger.info('No image found', imageInfo);
        throw new NoImageError();
      }
      // Disable processing on acquisition images loaded from PACS
      // Since PACS images are already stored in a processed state,
      // we don't want to process them again.
      // However, we do not have a more efficient way to determine if the image is in its processed form or not.
      if (imageInfo.origin === 'acquisition') {
        return adaptPicoxiaProcessedImageToImageSize(loadedImage, imageInfo);
      }
      return loadedImage;
    } catch (e) {
      console.error('Failed to load image', e);
      throw e;
    }
  };
}
